<template>
    <section class="overflow-anchor-none min-w-0">
        <AisStateResults>
            <template #default="slotProps">
                <div v-if="publishStateResults(slotProps)" class="hidden" />
            </template>
        </AisStateResults>

        <AisConfigure v-bind="categorySearch.configureSearchParameters.value" />

        <div :id="categorySearch.categoryTitleId" class="mb-2 scroll-mt-3 lg:text-center varify-inline-filters:lg:text-left">
            <h1
                :class="[
                    'text-2xl font-black wrap-break-word lg:text-3xl lg:font-bold lg:tracking-tight lg:text-zinc-950',
                    'varify-inline-filters:lg:text-2xl varify-inline-filters:lg:font-black varify-inline-filters:lg:tracking-normal varify-inline-filters:lg:text-inherit',
                ]"
                v-text="categorySearch.title.value"
            />
        </div>

        <CategoryHeaderDescription
            :description="categoryHeaderDescription"
            :expanded-on-desktop="categorySearch.expandDescriptionOnDesktop.value"
            :expanded-on-mobile="categorySearch.expandDescriptionOnMobile.value"
        />

        <CategoryLinksImagePills
            :hide-on-desktop="categorySearch.hideCategoryLinksOnDesktop.value"
            :hide-on-mobile="categorySearch.hideCategoryLinksOnMobile.value"
            :links="categorySearch.categoryLinks.value"
        />

        <AisStats>
            <template #default="{ nbHits }">
                <span v-if="publishNbHits(nbHits)" class="hidden" />
            </template>
        </AisStats>

        <div>
            <CategorySearchResultsToolbar
                :algolia-ready="pageStore.algoliaReady"
                :filters-enabled="categorySearch.hasFilterPanels.value"
                :grid-view="productStore.gridView"
                :nb-hits="currentNbHits"
                :result-label="categoryResultLabel"
                :result-text="categoryResultText"
                :selected-filter-count="selectedFilterCount"
                :show-title="false"
                :title="categorySearch.title.value"
                @open-filters="categorySearch.openMobileFilters"
                @update:grid-view="productStore.gridView = $event"
            >
                <div ref="headerFiltersRef" :style="headerFiltersMinHeightStyle" class="w-full transition-[min-height] duration-200 empty:hidden">
                    <CategorySearchHeaderFilters
                        :collection-filter-attribute="categorySearch.collectionFilterAttribute"
                        :header-filter-panels="categorySearch.headerFilterPanels.value"
                        :on-filter-applied="categorySearch.handleDesktopFilterApplied"
                    />
                </div>
            </CategorySearchResultsToolbar>
        </div>

        <!-- Publishes hits for the grid below. -->
        <AisHits>
            <template #default="{ items, sendEvent }">
                <span v-if="publishHits(items, sendEvent)" class="hidden" />
            </template>
        </AisHits>

        <div ref="productsAreaRef" :style="productsMinHeightStyle" class="transition-[min-height] duration-200">
            <CategoryResultsGrid
                :category-search="categorySearch"
                :has-published-hits="hasPublishedHits"
                :hits="algoliaHits"
                :initial-results="initialResults"
                :nb-hits="currentNbHits"
                :send-event="hitsSendEvent"
                :trust-initial-results="trustInitialResults"
            />
        </div>

        <CategorySearchMobileFloatingButton
            :nb-hits="currentNbHits"
            :visible="isMobileFloatingButtonVisible"
            :show-scroll-top="hasScrolledPastThreshold"
            @open-filters="categorySearch.openMobileFilters"
        />
    </section>
</template>

<script lang="ts" setup>
import type { useCategorySearch } from '@/composables/category/useCategorySearch';
import { useCategorySearchResultsSync } from '@/composables/category/useCategorySearchResultsSync';
import CategoryHeaderDescription from '@/pages/category/partials/CategoryHeaderDescription.vue';
import CategoryLinksImagePills from '@/pages/category/partials/CategoryLinksImagePills.vue';
import CategoryResultsGrid from '@/pages/category/partials/CategoryResultsGrid.vue';
import CategorySearchHeaderFilters from '@/pages/category/partials/CategorySearchHeaderFilters.vue';
import CategorySearchMobileFloatingButton from '@/pages/category/partials/CategorySearchMobileFloatingButton.vue';
import CategorySearchResultsToolbar from '@/pages/category/partials/CategorySearchResultsToolbar.vue';
import { categoryPageStore } from '@/stores/category/CategoryPageStore';
import { categoryProductStore } from '@/stores/category/CategoryProductStore';
import type { CategorySearchEventSender, CategorySearchResult, CategorySearchResultsState } from '@/types/category/CategorySearchTypes';
import type { CategoryInitialResultObject } from '@/types/generated';
import { useWindowScroll } from '@vueuse/core';
import { computed, inject, nextTick, ref, shallowRef, watch } from 'vue';
// @ts-expect-error TS7016 Vue instantsearch doesn't have typehints
import { AisConfigure, AisHits, AisStateResults, AisStats } from 'vue-instantsearch/vue3/es';

type CategoryStateResultsSlotProps = {
    results?: CategorySearchResultsState | null;
    status?: string;
};

const props = defineProps<{
    categorySearch: ReturnType<typeof useCategorySearch>;
    initialResults: CategoryInitialResultObject[];
    selectedFilterCount: number;
}>();

const pageStore = categoryPageStore();
const productStore = categoryProductStore();
const { syncAvailableFilters } = useCategorySearchResultsSync();

type InstantSearchInstance = {
    setUiState: (updater: (prevState: Record<string, { refinementList?: Record<string, string[]> }>) => Record<string, unknown>) => void;
};

const instantSearchInstance = inject<InstantSearchInstance>('$_ais_instantSearchInstance');

const algoliaHits = shallowRef<CategorySearchResult[]>([]);
// Set with the hits themselves, so it is not in step with the other widget slots.
const hasPublishedHits = ref<boolean>(false);
const hitsSendEvent = ref<CategorySearchEventSender | undefined>(undefined);
const currentNbHits = ref<number>(pageStore.initialNbHits);

// Null means the server could not resolve the URL's filters, so its hits do not match.
const trustInitialResults = computed((): boolean => pageStore.initialRefinements !== null);

function publishStateResults(slotProps: CategoryStateResultsSlotProps): true {
    return syncAvailableFilters(slotProps.results);
}

function publishHits(items: CategorySearchResult[], sendEvent: CategorySearchEventSender): true {
    algoliaHits.value = items;
    hitsSendEvent.value = sendEvent;
    hasPublishedHits.value = true;

    return true;
}

function publishNbHits(nbHits: number): true {
    currentNbHits.value = nbHits;

    return true;
}

const applyPendingDefaults = (newFilters: Record<string, Record<string, number>>): void => {
    if (Object.keys(newFilters).length === 0) {
        return;
    }

    if (!props.categorySearch.pendingDefaultRefinements.value) {
        return;
    }

    const index = productStore.productIndex;

    if (!index || !instantSearchInstance) {
        props.categorySearch.clearPendingDefaultRefinements();
        return;
    }

    const mappedDefaults = props.categorySearch.applyPendingDefaultRefinements(newFilters);

    if (Object.keys(mappedDefaults).length > 0) {
        instantSearchInstance.setUiState((prevState: Record<string, { refinementList?: Record<string, string[]> }>) => ({
            ...prevState,
            [index]: {
                ...prevState[index],
                refinementList: {
                    ...prevState[index]?.refinementList,
                    ...mappedDefaults,
                },
            },
        }));
    }

    props.categorySearch.clearPendingDefaultRefinements();
};

// Fallback for defaults routeToState could not resolve up front; usually does nothing.
const stopDefaultsWatcher = watch(
    () => pageStore.availableFilters,
    newFilters => {
        if (Object.keys(newFilters).length === 0) {
            return;
        }

        stopDefaultsWatcher();
        applyPendingDefaults(newFilters);
    },
);

const categoryHeaderDescription = computed((): string | undefined => pageStore.category?.settings?.header_description ?? undefined);

const categoryResultLabel = computed(() => {
    const settings = pageStore.category?.settings;

    if (!settings) {
        return undefined;
    }

    return currentNbHits.value === 1 ? (settings.result_label_singular ?? undefined) : (settings.result_label_plural ?? undefined);
});

const categoryResultText = computed((): string | undefined => {
    const settings = pageStore.category?.settings;

    if (!settings?.use_result_text) {
        return undefined;
    }

    return settings.result_text ?? undefined;
});
const headerFiltersRef = ref<HTMLElement | null>(null);
const headerFiltersMinHeightStyle = ref<{ minHeight: string } | undefined>(undefined);
const productsAreaRef = ref<HTMLElement | null>(null);
const productsMinHeightStyle = ref<{ minHeight: string } | undefined>(undefined);

function releaseLock(
    elRef: Readonly<ReturnType<typeof ref<HTMLElement | null>>>,
    styleRef: ReturnType<typeof ref<{ minHeight: string } | undefined>>,
    lockedHeight: number,
): void {
    const realHeight = elRef.value?.offsetHeight ?? 0;

    if (realHeight >= lockedHeight) {
        styleRef.value = undefined;
        return;
    }

    styleRef.value = { minHeight: `${realHeight}px` };
    setTimeout(() => {
        styleRef.value = undefined;
    }, 220);
}

watch(
    () => pageStore.algoliaReady,
    async (ready: boolean) => {
        if (!ready) {
            return;
        }

        // Capture skeleton heights before any tick, while Vue still shows the skeleton.
        const lockedHeaderHeight = headerFiltersRef.value?.offsetHeight ?? 0;
        const lockedProductsHeight = productsAreaRef.value?.offsetHeight ?? 0;

        if (lockedHeaderHeight > 0) {
            headerFiltersMinHeightStyle.value = { minHeight: `${lockedHeaderHeight}px` };
        }

        if (lockedProductsHeight > 0) {
            productsMinHeightStyle.value = { minHeight: `${lockedProductsHeight}px` };
        }

        // One shared tick — Vue re-renders both areas simultaneously.
        await nextTick();

        releaseLock(headerFiltersRef, headerFiltersMinHeightStyle, lockedHeaderHeight);
        releaseLock(productsAreaRef, productsMinHeightStyle, lockedProductsHeight);
    },
);
const { y: windowScrollY } = useWindowScroll();
const floatingButtonScrollThreshold = 150;

const hasScrolledPastThreshold = computed((): boolean => windowScrollY.value > floatingButtonScrollThreshold);

const isMobileFloatingButtonVisible = computed(
    (): boolean => props.categorySearch.hasFilterPanels.value && !props.categorySearch.isMobileFiltersOpen.value && hasScrolledPastThreshold.value,
);
</script>
